import i18n from "@shared/i18n";
import { ApiError, isApiError, isApiErrorLike, toApiError, type ApiErrorPayload } from "./types";

export interface TokenStore {
  getAccess(): Promise<string | null>;
  getAccessSync(): string | null;
  setAccess(token: string | null): Promise<void>;
  getRefresh(): Promise<string | null>;
  setRefresh(token: string | null): Promise<void>;
  clear(): Promise<void>;
  migrateFromWebStorageIfNeeded(): Promise<void>;
}

export type AuthChangeReason = "login" | "logout" | "refresh" | "expired" | "manual";

export type AuthChangeEvent = {
  accessToken: string | null;
  refreshToken: string | null;
  reason: AuthChangeReason;
};

export type ApiClientOptions = {
  baseUrl?: string;
  apiToken?: string;
  tokenStore?: TokenStore;
};

type RequestOptions = RequestInit & {
  auth?: boolean;
  retryOnUnauthorized?: boolean;
};

type AuthListener = (event: AuthChangeEvent) => void;

/**
 * Synchronous fallback token store (legacy localStorage) used until the
 * platform-aware tokenStore is registered via `setTokenStore`.
 *
 * The platform store is registered at module top-level by the host app
 * (see `frontend/src/main.tsx`), so by the time any request is fired the
 * real adapter is already in place.
 */
const ACCESS_TOKEN_KEY = "auth_access_token";
const REFRESH_TOKEN_KEY = "auth_refresh_token";
const memoryTokenFallback = new Map<string, string>();

function legacyStorage() {
  try {
    if (typeof localStorage !== "undefined") return localStorage;
  } catch {
    // Some browser contexts expose storage but throw when it is accessed.
  }
  return {
    getItem: (key: string) => memoryTokenFallback.get(key) ?? null,
    setItem: (key: string, value: string) => {
      memoryTokenFallback.set(key, value);
    },
    removeItem: (key: string) => {
      memoryTokenFallback.delete(key);
    },
  };
}

const legacySyncTokenStore: TokenStore = {
  async getAccess() {
    return legacyStorage().getItem(ACCESS_TOKEN_KEY);
  },
  getAccessSync() {
    return legacyStorage().getItem(ACCESS_TOKEN_KEY);
  },
  async setAccess(token) {
    if (token === null) legacyStorage().removeItem(ACCESS_TOKEN_KEY);
    else legacyStorage().setItem(ACCESS_TOKEN_KEY, token);
  },
  async getRefresh() {
    return legacyStorage().getItem(REFRESH_TOKEN_KEY);
  },
  async setRefresh(token) {
    if (token === null) legacyStorage().removeItem(REFRESH_TOKEN_KEY);
    else legacyStorage().setItem(REFRESH_TOKEN_KEY, token);
  },
  async clear() {
    legacyStorage().removeItem(ACCESS_TOKEN_KEY);
    legacyStorage().removeItem(REFRESH_TOKEN_KEY);
  },
  async migrateFromWebStorageIfNeeded() {},
};

let activeTokenStore: TokenStore = legacySyncTokenStore;

export function setTokenStore(store: TokenStore): void {
  activeTokenStore = store;
}

export function getTokenStore(): TokenStore {
  return activeTokenStore;
}

function getLanguage(): string {
  return i18n?.language || (typeof navigator !== "undefined" ? navigator.language : "en");
}

async function responsePayload(response: Response): Promise<ApiErrorPayload | unknown> {
  const contentType = response.headers.get("Content-Type") || "";
  if (contentType.includes("application/json")) {
    return response.json().catch(() => ({}));
  }
  return response.text().catch(() => "");
}

function messageFromPayload(payload: unknown, fallback: string): string {
  if (payload && typeof payload === "object") {
    const body = payload as ApiErrorPayload;
    if (typeof body.detail === "string") return body.detail;
    if (typeof body.error === "string") return body.error;
    if (typeof body.message === "string") return body.message;
    if (typeof body.code === "string") return body.code;
  }
  if (typeof payload === "string" && payload.trim()) return payload;
  return fallback;
}

/** Header the backend sets on a 404 for a disabled site feature. */
export const FEATURE_DISABLED_HEADER = "X-Feature-Disabled";
/** Window event fired (once per burst) when a response carries that header. */
export const FEATURE_DISABLED_EVENT = "site-features:disabled";
const FEATURE_DISABLED_BURST_MS = 1000;
let featureDisabledPending = false;

/**
 * A request hit a feature the admin just switched off: tell the app to
 * refetch its flags so the stale nav item disappears. Bursts (a page firing
 * several requests at the same disabled API) collapse into one event.
 */
export function notifyIfFeatureDisabled(response: Response): void {
  const feature = response.headers?.get?.(FEATURE_DISABLED_HEADER);
  if (!feature || featureDisabledPending || typeof window === "undefined") return;
  featureDisabledPending = true;
  window.dispatchEvent(
    new CustomEvent(FEATURE_DISABLED_EVENT, { detail: { feature }, bubbles: false }),
  );
  setTimeout(() => {
    featureDisabledPending = false;
  }, FEATURE_DISABLED_BURST_MS);
}

export class ApiClient {
  private baseUrl: string;
  private apiToken: string;
  private listeners = new Set<AuthListener>();
  private refreshPromise: Promise<string | null> | null = null;

  constructor(options: ApiClientOptions = {}) {
    this.baseUrl = options.baseUrl ?? import.meta.env.VITE_API_URL ?? "";
    this.apiToken = options.apiToken ?? import.meta.env.VITE_API_TOKEN ?? "";
    if (options.tokenStore) setTokenStore(options.tokenStore);
  }

  /** Synchronous read — backed by either localStorage (web) or in-memory (native). */
  getAccessToken(): string | null {
    return activeTokenStore.getAccessSync();
  }

  async getAccessTokenAsync(): Promise<string | null> {
    return activeTokenStore.getAccess();
  }

  getBaseUrl(): string {
    return this.baseUrl;
  }

  async getRefreshToken(): Promise<string | null> {
    return activeTokenStore.getRefresh();
  }

  async setAuthTokens(
    accessToken: string,
    refreshToken: string,
    reason: AuthChangeReason = "manual",
  ): Promise<void> {
    await activeTokenStore.setAccess(accessToken);
    await activeTokenStore.setRefresh(refreshToken);
    this.emitAuthChange(reason);
  }

  async clearAuth(reason: AuthChangeReason = "logout"): Promise<void> {
    await activeTokenStore.clear();
    this.emitAuthChange(reason);
  }

  onAuthChange(listener: AuthListener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  async refreshAccessToken(): Promise<string | null> {
    if (this.refreshPromise) return this.refreshPromise;

    this.refreshPromise = (async () => {
      const refreshToken = await activeTokenStore.getRefresh();
      if (!refreshToken) return null;
      try {
        const result = await this.post<{ access: string; refresh?: string }>(
          "/api/v1/auth/refresh/",
          { refresh: refreshToken },
          { auth: false, retryOnUnauthorized: false },
        );
        if (!result.access) return null;
        const nextRefresh = result.refresh ?? refreshToken;
        await this.setAuthTokens(result.access, nextRefresh, "refresh");
        return result.access;
      } catch {
        await this.clearAuth("expired");
        return null;
      } finally {
        this.refreshPromise = null;
      }
    })();

    return this.refreshPromise;
  }

  get<T>(path: string, options?: RequestOptions): Promise<T> {
    return this.request<T>(path, { ...options, method: "GET" });
  }

  post<T>(path: string, body?: unknown, options?: RequestOptions): Promise<T> {
    return this.request<T>(path, {
      ...options,
      method: "POST",
      body: body instanceof FormData ? body : body === undefined ? undefined : JSON.stringify(body),
    });
  }

  put<T>(path: string, body?: unknown, options?: RequestOptions): Promise<T> {
    return this.request<T>(path, {
      ...options,
      method: "PUT",
      body: body instanceof FormData ? body : body === undefined ? undefined : JSON.stringify(body),
    });
  }

  patch<T>(path: string, body?: unknown, options?: RequestOptions): Promise<T> {
    return this.request<T>(path, {
      ...options,
      method: "PATCH",
      body: body instanceof FormData ? body : body === undefined ? undefined : JSON.stringify(body),
    });
  }

  delete<T>(path: string, options?: RequestOptions): Promise<T> {
    return this.request<T>(path, { ...options, method: "DELETE" });
  }

  requestForm<T>(path: string, formData: FormData, options?: RequestOptions): Promise<T> {
    return this.request<T>(path, { ...options, body: formData });
  }

  async getBlob(path: string, options?: RequestOptions): Promise<Blob> {
    const response = await this.rawRequest(path, { ...options, method: "GET" });
    if (!response.ok) throw await this.buildError(response);
    return response.blob();
  }

  async postBlob(path: string, body?: unknown, options?: RequestOptions): Promise<Blob> {
    const response = await this.rawRequest(path, {
      ...options,
      method: "POST",
      body: body instanceof FormData ? body : body === undefined ? undefined : JSON.stringify(body),
    });
    if (!response.ok) throw await this.buildError(response);
    return response.blob();
  }

  upload<T>(
    path: string,
    formData: FormData,
    options: RequestOptions & { onProgress?: (percent: number) => void } = {},
  ): Promise<T> {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open(options.method || "POST", this.toUrl(path));
      xhr.withCredentials = true;
      xhr.timeout = 120000;

      for (const [key, value] of Object.entries(this.headers({ ...options, body: formData }))) {
        xhr.setRequestHeader(key, value);
      }

      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable && options.onProgress) {
          options.onProgress(Math.round((event.loaded / event.total) * 100));
        }
      };

      xhr.onload = () => {
        const payload = xhr.responseText ? JSON.parse(xhr.responseText) : undefined;
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve(payload as T);
          return;
        }
        reject(new ApiError(messageFromPayload(payload, xhr.statusText), xhr.status, payload as ApiErrorPayload));
      };
      xhr.onerror = () => reject(new ApiError("Network error"));
      xhr.ontimeout = () => reject(new ApiError("Request timed out"));
      xhr.onabort = () => reject(new ApiError("Request aborted"));
      xhr.send(formData);
    });
  }

  private async request<T>(path: string, options: RequestOptions = {}): Promise<T> {
    const retryOnUnauthorized = options.retryOnUnauthorized ?? true;
    const response = await this.rawRequest(path, options);

    if (response.status === 401 && retryOnUnauthorized && options.auth !== false) {
      const refreshed = await this.refreshAccessToken();
      if (refreshed) {
        const retry = await this.rawRequest(path, options);
        if (!retry.ok) throw await this.buildError(retry);
        return this.parseResponse<T>(retry);
      }
    }

    if (response.status === 402) {
      const payload = await responsePayload(response);
      if (
        typeof payload === "object" &&
        payload !== null &&
        "code" in payload &&
        (payload as Record<string, unknown>)["code"] === "insufficient_credits"
      ) {
        const body = payload as Record<string, unknown>;
        if (typeof window !== "undefined") {
          window.dispatchEvent(
            new CustomEvent("payment:insufficient-credits", {
              detail: {
                required:
                  typeof body["required"] === "number"
                    ? body["required"]
                    : typeof body["requiredCents"] === "number"
                      ? body["requiredCents"]
                      : undefined,
                available:
                  typeof body["available"] === "number"
                    ? body["available"]
                    : typeof body["availableCents"] === "number"
                      ? body["availableCents"]
                      : undefined,
              },
              bubbles: false,
            })
          );
        }
      }
      throw new ApiError(
        messageFromPayload(payload, response.statusText),
        response.status,
        payload as ApiErrorPayload
      );
    }

    if (!response.ok) throw await this.buildError(response);
    return this.parseResponse<T>(response);
  }

  private async rawRequest(path: string, options: RequestOptions): Promise<Response> {
    const response = await fetch(this.toUrl(path), {
      credentials: "include",
      ...options,
      headers: this.headers(options),
    });
    if (response.status === 404) notifyIfFeatureDisabled(response);
    return response;
  }

  private headers(options: RequestOptions): Record<string, string> {
    const headers: Record<string, string> = {
      Accept: "application/json",
      "Accept-Language": getLanguage(),
      ...(options.headers as Record<string, string> | undefined),
    };

    if (!(options.body instanceof FormData) && options.body !== undefined && !headers["Content-Type"]) {
      headers["Content-Type"] = "application/json";
    }

    if (this.apiToken && !headers.Authorization) {
      headers.Authorization = `Bearer ${this.apiToken}`;
    }

    const accessToken = options.auth === false ? null : this.getAccessToken();
    if (accessToken) headers.Authorization = `Bearer ${accessToken}`;
    return headers;
  }

  private toUrl(path: string): string {
    if (/^https?:\/\//.test(path)) return path;
    return `${this.baseUrl}${path}`;
  }

  private async parseResponse<T>(response: Response): Promise<T> {
    if (response.status === 204) return undefined as T;
    return responsePayload(response) as Promise<T>;
  }

  private async buildError(response: Response): Promise<ApiError> {
    const payload = await responsePayload(response);
    return new ApiError(messageFromPayload(payload, response.statusText), response.status, payload as ApiErrorPayload);
  }

  private emitAuthChange(reason: AuthChangeReason) {
    // Listeners only need to know SOMETHING changed; the access token is
    // available synchronously and a fresh listener call should reflect it.
    // The refresh token field is intentionally omitted — listeners that
    // care can call `getRefreshToken()` themselves.
    const event: AuthChangeEvent = {
      accessToken: this.getAccessToken(),
      refreshToken: null,
      reason,
    };
    this.listeners.forEach((listener) => listener(event));
  }
}

export const apiClient = new ApiClient();
export { ApiError, isApiError, isApiErrorLike, toApiError };
export type { ApiErrorPayload };
